import { api } from './api';
import { getAssetUrl } from "../../components/Public/utils";

export interface MappedJob {
  id: string;
  title: string;
  description: string;
  salary: string;
  vacancies: number;
  deadline?: string;
  expired: boolean;
  createdAt: string;
  status: string;
  employmentType: string;
  seniorityLevel: string;
  locationType: string;
  location: string;
  countryId?: string;
  regionId?: string;
  employmentTypeId?: string;
  seniorityLevelId?: string;
  locationTypeId?: string;
  sectorIds?: string[];
  jobSectors?: { id: string; type: string }[];
  company: {
    id: string;
    name: string;
    description: string;
    logo: string;
  };
  tags: string[];
}

export const mapJobData = (job: any): MappedJob | null => {
  if (!job) return null;

  const company = job.company_user?.company;
  return {
    id: job.id,
    title: job.title || "Untitled Position",
    description: job.description ?? "",
    salary: job.salary,
    vacancies: job.vacancies || 0,
    deadline: job.deadline,
    expired: job.deadline ? new Date(job.deadline) < new Date() : false,
    createdAt: job.created_at || job.createdAt,
    status: job.job_status?.status || "unknown",
    employmentType: job.job_employment_type?.type || "Full-time",
    seniorityLevel: job.job_seniority_level?.type || "Not Specified",
    locationType: job.job_location_type?.type || "On-site",
    location: [job.location?.region?.name, job.location?.country?.name]
      .filter(Boolean)
      .join(", ") || "Unknown",
    countryId: job.location?.country?.id,
    regionId: job.location?.region?.id,
    employmentTypeId: job.job_employment_type?.id,
    seniorityLevelId: job.job_seniority_level?.id,
    locationTypeId: job.job_location_type?.id,
    sectorIds: job.job_sectors?.map((s: any) => s.id) || [],
    jobSectors: job.job_sectors,
    company: {
      id: company?.id || "",
      name: company?.name || "Unknown Company",
      description: company?.description || "",
      logo: getAssetUrl(company?.logo_file?.attachment?.url),
    },
    tags: [
      job.job_employment_type?.type,
      job.job_location_type?.type,
      job.job_seniority_level?.type,
    ].filter(Boolean),
  };
};

export const jobApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createJob: builder.mutation({
      query: (data: any) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation CreateJob($data: JobCreateInput!) {
              createJob(data: $data) {
                id
                title
              }
            }
          `,
          variables: { data },
        },
      }),
      invalidatesTags: ['Company'],
    }),
    getJobMetadata: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetJobMetadata {
              employmentTypes {
                id
                type
              }
              seniorityLevelTypes {
                id
                type
              }
              jobLocationTypes {
                id
                type
              }
              sectorTypes {
                id
                type
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => ({
        employmentTypes: response.data.employmentTypes,
        seniorityLevelTypes: response.data.seniorityLevelTypes,
        jobLocationTypes: response.data.jobLocationTypes,
        sectorTypes: response.data.sectorTypes,
      }),
    }),
    getJob: builder.query({
      query: (id: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetJob($id: ID!) {
              job(where: { id: $id }) {
                id
                title
                description
                salary
                vacancies
                deadline
                created_at
                job_sectors {
                  id
                  type
                }
                job_status {
                  id
                  status
                }
                job_employment_type {
                  id
                  type
                }
                job_seniority_level {
                  id
                  type
                }
                job_location_type {
                  id
                  type
                }
                location {
                  id
                  country {
                    id
                    name
                  }
                  region {
                    id
                    name
                  }
                }
                company_user {
                  id
                  company {
                    id
                    name
                    description
                    logo_file {
                      id
                      attachment {
                        url
                        filename
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: { id },
        },
      }),
      transformResponse: (response: any) => mapJobData(response.data?.job),
      providesTags: (result, error, id) => [{ type: 'Job', id }],
    }),
    updateJob: builder.mutation({
      query: ({ id, data }: { id: string; data: any }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation UpdateJob($id: ID!, $data: JobUpdateInput!) {
              updateJob(where: { id: $id }, data: $data) {
                id
                title
                job_status {
                    status
                }
              }
            }
          `,
          variables: { id, data },
        },
      }),
      invalidatesTags: (result, error, { id }) => ['Company', { type: 'Job', id }],
    }),
    updateJobStatus: builder.mutation({
      query: ({ id, status }: { id: string; status: string }) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
                mutation UpdateJobStatus($id: ID!, $status: String!) {
                    updateJob(
                        where: { id: $id }
                        data: {
                            job_status: {
                                connect: {
                                    status: $status
                                }
                            }
                        }
                    ) {
                        id
                        job_status {
                            id
                            status
                        }
                    }
                }
            `,
          variables: { id, status }
        }
      }),
      transformResponse: (response: any) => {
        // Return the response data structure
        return response.data || response;
      },
      invalidatesTags: (result, error, { id }) => ['Company', { type: 'Job', id }],
    }),
    closeJobAndSuspendUser: builder.mutation({
      query: (id: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            mutation AdministrativeSanction($jobId: ID!) {
              closeJobAndSuspendUser(jobId: $jobId)
            }
          `,
          variables: { jobId: id },
        },
      }),
      transformResponse: (response: any) => response.data?.closeJobAndSuspendUser,
      invalidatesTags: ['Company', 'Job'],
    }),
    getCompanyUserId: builder.query({
      query: (userId: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
                    query GetCompanyUserId($userId: ID!) {
                        companyUsers(where: { user: { id: { equals: $userId } } }) {
                            id
                        }
                    }
                `,
          variables: { userId }
        }
      }),
      transformResponse: (response: any) => {
        const users = response.data.companyUsers;
        return users && users.length > 0 ? users[0].id : null;
      }
    }),
    // Admin endpoints
    getAllCompanies: builder.query({
      query: () => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetAllCompanies {
              companyUsers {
                company {
                  id
                  name
                }
              }
            }
          `,
        },
      }),
      transformResponse: (response: any) => {
        const rows = response.data.companyUsers || [];
        const byId = new Map<string, { id: string; name: string }>();
        rows.forEach((row: any) => {
          const company = row?.company;
          if (company?.id && !byId.has(company.id)) {
            byId.set(company.id, { id: company.id, name: company.name });
          }
        });
        return Array.from(byId.values()).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
      },
      providesTags: ['Company'],
    }),
    getAllJobs: builder.query({
      query: ({
        take = 10,
        skip = 0,
        companyId,
        searchText,
        openOnly = false
      }: {
        take?: number;
        skip?: number;
        companyId?: string;
        searchText?: string;
        openOnly?: boolean;
      }) => {
        const whereClause: any = {};

        if (companyId) {
          whereClause.company_user = {
            company: { id: { equals: companyId } }
          };
        }

        if (openOnly) {
          whereClause.job_status = {
            status: { equals: "open" }
          };
        }

        if (searchText && searchText.trim()) {
          whereClause.OR = [
            { title: { contains: searchText, mode: 'insensitive' } },
            { description: { contains: searchText } }
          ];
        }

        return {
          url: '',
          method: 'POST',
          body: {
            query: `
              query GetAllJobs($where: JobWhereInput!, $take: Int, $skip: Int!) {
                jobs(
                  where: $where
                  orderBy: { created_at: desc }
                  take: $take
                  skip: $skip
                ) {
                  id
                  title
                  description
                  salary
                  vacancies
                  deadline
                  created_at
                  job_status {
                    id
                    status
                  }
                  job_employment_type {
                    id
                    type
                  }
                  job_seniority_level {
                    id
                    type
                  }
                  location {
                    region {
                      id
                      name
                    }
                    country {
                      id
                      name
                    }
                  }
                  job_sectors {
                    id
                    type
                  }
                  company_user {
                    company {
                      id
                      name
                      logo_file {
                        id
                        attachment {
                          url
                          filename
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { where: whereClause, take, skip },
          },
        };
      },
      transformResponse: (response: any) => (response.data?.jobs || []).map(mapJobData),
      providesTags: ['Company'],
    }),
    getJobsCount: builder.query({
      query: ({ companyId, searchText, openOnly = false }: { companyId?: string; searchText?: string; openOnly?: boolean } = {}) => {
        const whereClause: any = {};

        if (companyId) {
          whereClause.company_user = {
            company: { id: { equals: companyId } }
          };
        }

        if (openOnly) {
          whereClause.job_status = {
            status: { equals: "open" }
          };
        }

        if (searchText && searchText.trim()) {
          whereClause.OR = [
            { title: { contains: searchText, mode: 'insensitive' } },
            { description: { contains: searchText } }
          ];
        }

        return {
          url: '',
          method: 'POST',
          body: {
            query: `
              query GetJobsCount($where: JobWhereInput!) {
                jobsCount(where: $where)
              }
            `,
            variables: { where: whereClause },
          },
        };
      },
      transformResponse: (response: any) => response.data.jobsCount,
      providesTags: ['Company'],
    }),
    getCompanyUserByCompany: builder.query({
      query: (companyId: string) => ({
        url: '',
        method: 'POST',
        body: {
          query: `
            query GetCompanyUserByCompany($companyId: ID!) {
              companyUsers(where: { company: { id: { equals: $companyId } } }, take: 1) {
                id
                company {
                  id
                  name
                }
              }
            }
          `,
          variables: { companyId },
        },
      }),
      transformResponse: (response: any) => {
        const users = response.data.companyUsers;
        return users && users.length > 0 ? users[0] : null;
      },
    }),
  }),
});

export const {
  useCreateJobMutation,
  useGetJobMetadataQuery,
  useGetCompanyUserIdQuery,
  useGetJobQuery,
  useUpdateJobMutation,
  useUpdateJobStatusMutation,
  useCloseJobAndSuspendUserMutation,
  useGetAllCompaniesQuery,
  useGetAllJobsQuery,
  useGetJobsCountQuery,
  useGetCompanyUserByCompanyQuery,
} = jobApi;
